import React from 'react';
import { CurrencyInput, Field } from 'ui';
import Tooltip from '~/components/Form/Tooltip';
import HelpButton from '~/components/Links/HelpButton';
import ListFlowSection from '~/components/ListFlow/ListFlowSection';
import useHasFeature from '~/hooks/useHasFeature';
import {
  changedPrivateDetail,
  getListFlowPrivateDetails,
} from '~/store/listFlow/slices/info';
import {
  getListFlowFieldErrorMap,
  getListFlowItemSource,
} from '~/store/listFlow/ui';
import { useAppDispatch, useAppSelector } from '~/store/store';

interface Props {}

const ListFlowPrivateDetailsSection: React.FC<Props> = () => {
  const dispatch = useAppDispatch();
  const privateDetails = useAppSelector(getListFlowPrivateDetails);
  const errors = useAppSelector(getListFlowFieldErrorMap);
  const canListMyCost = useHasFeature('list_my_cost');
  const canListInventoryId = useHasFeature('list_inventory_id');
  const canListGtin = useHasFeature('list_gtin');
  const itemSource = useAppSelector(getListFlowItemSource);

  if (!canListMyCost && !canListGtin && !canListInventoryId) {
    return null;
  }

  return (
    <ListFlowSection title="Private Details">
      <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2 lg:grid-cols-4">
        {canListMyCost && (
          <Field
            label={
              <span>
                My Cost
                <Tooltip tooltip="Keep track of your own costs and margins">
                  <HelpButton className="ml-0" />
                </Tooltip>
              </span>
            }
            error={errors?.seller_total_cost}
          >
            <CurrencyInput
              onChange={value =>
                dispatch(changedPrivateDetail({ detail: 'myCost', value }))
              }
              value={privateDetails.myCost ?? ''}
            />
          </Field>
        )}

        {canListGtin && (
          <Field
            label={
              <span>
                GTIN (UPC)
                <Tooltip tooltip="Global Trade Item Numbers are used by Google and other search engines to help buyers find your item">
                  <HelpButton className="ml-0" />
                </Tooltip>
              </span>
            }
            error={errors?.gtin}
            onChange={e =>
              dispatch(
                changedPrivateDetail({ detail: 'gtin', value: e.target.value }),
              )
            }
            value={privateDetails.gtin ?? ''}
          />
        )}

        {canListGtin && (
          <Field
            label={
              <span>
                MPN
                <Tooltip tooltip="Manufacturing Part Numbers are used by Google and other search engines to help buyers find your item">
                  <HelpButton className="ml-0" />
                </Tooltip>
              </span>
            }
            error={errors?.mpn}
            onChange={e =>
              dispatch(
                changedPrivateDetail({ detail: 'mpn', value: e.target.value }),
              )
            }
            value={privateDetails.mpn ?? ''}
          />
        )}

        {canListInventoryId && (
          <Field
            autoComplete="off"
            disabled={itemSource === 'import'}
            label={
              <span>
                Inventory ID
                <Tooltip
                  tooltip={`Inventory ID's can help you track inventory across databases.${
                    itemSource === 'import'
                      ? ' Not editable on imported items'
                      : ''
                  }`}
                >
                  <HelpButton className="ml-0" />
                </Tooltip>
              </span>
            }
            error={errors?.inventory_id}
            onChange={e =>
              dispatch(
                changedPrivateDetail({
                  detail: 'inventoryId',
                  value: e.target.value,
                }),
              )
            }
            value={privateDetails.inventoryId ?? ''}
          />
        )}
      </div>
    </ListFlowSection>
  );
};

export default ListFlowPrivateDetailsSection;
